import type { PrizeDistributionRowDto } from "../model/types";
import { translateUi } from "@/shared/lib/ui-translate";

export function getPrizePlacesRange(
  rows: PrizeDistributionRowDto[] | null | undefined,
): { from: number; to: number } | null {
  if (!rows?.length) return null;
  const first = rows[0];
  if (!first) return null;
  let from = first.placeFrom;
  let to = first.placeTo;
  for (const r of rows) {
    if (r.placeFrom < from) from = r.placeFrom;
    if (r.placeTo > to) to = r.placeTo;
  }
  return { from, to };
}

export function getPrizeTypeLabel(
  rows: PrizeDistributionRowDto[] | null | undefined,
): string {
  if (!rows?.length) return translateUi("litPrizeDisplay.denezhnye");
  const types = new Set<string>();
  for (const r of rows) {
    const t = String(r.prizeType ?? "Money").trim();
    const lower = t.toLowerCase();
    const normalized = lower === "money" ? "Money" : lower === "ingame" ? "InGame" : lower === "physical" ? "Physical" : t;
    if (normalized === "Money") types.add(translateUi("litPrizeDisplay.denezhnye"));
    else if (normalized === "InGame") types.add(translateUi("litPrizeDisplay.vnutriigrovye"));
    else if (normalized === "Physical") types.add(translateUi("litPrizeDisplay.fizicheskie"));
    else types.add(translateUi("litPrizeDisplay.prochee"));
  }
  return Array.from(types).sort().join(translateUi("litPrizeDisplay.razdelitelI")) || translateUi("litPrizeDisplay.denezhnye");
}

export function getTournamentDisplayPrizePool(
  prizePool: number | null | undefined,
  prizeDistribution: PrizeDistributionRowDto[] | null | undefined,
): number {
  const pool = Number(prizePool) || 0;
  const dist = prizeDistribution ?? [];
  const hasMoneyShares =
    pool > 0 &&
    dist.some(
      (r) =>
        r.prizeType === "Money" &&
        r.sharePercent != null &&
        Number(r.sharePercent) > 0,
    );
  if (hasMoneyShares) {
    const sum = dist
      .filter((r) => r.prizeType === "Money" && r.sharePercent != null)
      .reduce((s, r) => s + pool * (Number(r.sharePercent) || 0) / 100, 0);
    if (sum > 0) return sum;
  }
  return pool;
}

export function getTournamentDisplayPrizeLabel(
  prizePool: number | null | undefined,
  prizeDistribution: PrizeDistributionRowDto[] | null | undefined,
): { type: "money"; value: number } | { type: "prizes"; summary: string; prizeTypeLabel: string; placesRange: string } {
  const displayPool = getTournamentDisplayPrizePool(prizePool, prizeDistribution);
  const dist = prizeDistribution ?? [];
  if (displayPool > 0) {
    return { type: "money", value: displayPool };
  }
  if (dist.length > 0) {
    const range = getPrizePlacesRange(dist);
    const placesRange =
      range && range.from !== range.to
        ? translateUi("litPrizeDisplay.sPoMesto", { from: range.from, to: range.to })
        : range
          ? translateUi("litPrizeDisplay.mesto", { place: range.from })
          : "";
    return {
      type: "prizes",
      summary: summarizePrizeDistribution(dist, 5),
      prizeTypeLabel: getPrizeTypeLabel(dist),
      placesRange,
    };
  }
  return { type: "money", value: 0 };
}

export function summarizePrizeDistribution(
  rows: PrizeDistributionRowDto[] | null | undefined,
  maxParts = 3,
): string {
  if (!rows?.length) return "";
  const parts = rows.slice(0, maxParts).map((r) => {
    const place =
      r.placeFrom === r.placeTo
        ? String(r.placeFrom)
        : `${r.placeFrom}–${r.placeTo}`;
    if (r.prizeType === "Money" && r.sharePercent != null) {
      return `${place}: ${r.sharePercent}%`;
    }
    if (r.prizeType === "InGame" && r.quantity != null && r.description?.trim()) {
      return `${place}: ${r.quantity} ${r.description.trim()}`;
    }
    if (r.description?.trim()) return `${place}: ${r.description.trim()}`;
    const t =
      r.prizeType === "InGame" ? translateUi("litPrizeDisplay.igrovoy") : r.prizeType === "Physical" ? translateUi("litPrizeDisplay.predmet") : "";
    return t ? `${place}: ${t}` : place;
  });
  const more = rows.length > maxParts ? ` +${rows.length - maxParts}` : "";
  return parts.join(" · ") + more;
}
